/**
 * ChatAramaAfisi.js — Sohbet içi "devam eden arama" şeridi
 * ---------------------------------------------------------------------------
 * Sohbet odasının üstünde ince bir şerit: kaç kişi konuşuyor, ne kadar
 * süredir ve **Katıl** butonu.
 *
 * NEDEN AYRI BİR BİLEŞEN:
 *   Asıl arama arayüzü (gelen/aktif ekranlar) artık uygulama kökündeki
 *   `AramaEkrani` içinde. Ama sohbet odasına girdiğinde "burada bir arama var,
 *   katılabilirsin" bilgisini görmek gerekiyor — tam ekran açmak yerine bu
 *   ince şerit gösteriliyor. Kullanıcı isteği de buydu.
 *
 * Odada arama YOKSA veya kullanıcı ZATEN aramadaysa hiçbir şey render etmez.
 * Polling yapmaz; durumu merkezi yöneticiden okur.
 * ---------------------------------------------------------------------------
 */

import React, { useEffect, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import AvatarImg from "./AvatarImg";
import callManager from "../services/callManager";

function sureFormatla(ms) {
  const t = Math.max(0, Math.floor(ms / 1000));
  const dk = Math.floor(t / 60);
  const sn = t % 60;
  return `${String(dk).padStart(2, "0")}:${String(sn).padStart(2, "0")}`;
}

export default function ChatAramaAfisi({ roomId, uyeler = [], uyeRenk }) {
  const { t } = useLanguage();
  const [d, setD] = useState(() => callManager.durumuAl());
  const [gecen, setGecen] = useState(0);

  useEffect(() => callManager.aboneOl(setD), []);

  // Oda açılırken canlı arama var mı — TEK SEFER, polling değil.
  useEffect(() => {
    if (roomId) callManager.odayiKontrolEt(roomId);
  }, [roomId]);

  useEffect(() => {
    if (!d.baslangic) return undefined;
    const f = () => setGecen(Date.now() - d.baslangic);
    f();
    const id = setInterval(f, 1000);
    return () => clearInterval(id);
  }, [d.baslangic]);

  if (!roomId) return null;
  if (String(d.roomId || "") !== String(roomId)) return null;

  /* KONUŞMA SÜRERKEN DE GÖSTER.
     Kullanıcı arama ekranını küçültüp sohbete dönebiliyor; o sırada "bu odada
     konuşma sürüyor" bilgisinin ekranda kalması gerekiyor. Dokununca arama
     ekranı geri açılır. */
  if (d.durum === "aktif" || d.durum === "giden" || d.durum === "baglaniyor") {
    return (
      <button className="callBanner callBannerActive" onClick={() => callManager.ekraniAc()}>
        <span className="callBarDot" aria-hidden="true" />
        <div className="callBarText">
          <span className="callBarTitle">{t("call.voiceCall")}</span>
          <span className="callBarMeta">
            {d.durum === "giden"
              ? t("call.calling")
              : (d.durum === "baglaniyor" ? t("call.connecting") : sureFormatla(gecen))}
          </span>
        </div>
        <span className="callJoinBtn">{t("call.open")}</span>
      </button>
    );
  }

  if (d.durum !== "gelen") return null;
  if (!d.gelen?.devamEden) return null;          // gerçek gelen arama → tam ekran

  const katilanlar = (d.arama?.participants || []).filter((p) => p.state === "joined");
  const enFazla = d.arama?.maxParticipants || 6;
  const bagli = d.arama?.joinedCount ?? katilanlar.length;
  const dolu = bagli >= enFazla;

  return (
    <div className="callBanner">
      <span className="callBarDot" aria-hidden="true" />

      <div className="callBannerAvatars" aria-hidden="true">
        {katilanlar.slice(0, 3).map((p, i) => {
          const u = uyeler.find((x) => String(x.uid) === String(p.uid));
          return (
            <AvatarImg
              key={p.uid}
              avatar={u?.avatar || null}
              renk={uyeRenk && u ? uyeRenk(u.uid, uyeler) : null}
              initials={u?.ad?.[0] || "?"}
              size={22}
              radius="50%"
              uid={p.uid}
              style={{ marginLeft: i === 0 ? 0 : -7, zIndex: 5 - i }}
            />
          );
        })}
      </div>

      <div className="callBarText">
        <span className="callBarTitle">{t("call.ongoing")}</span>
        <span className="callBarMeta">
          {bagli > 0 ? `${bagli}/${enFazla} ${t("call.people")} · ${sureFormatla(gecen)}` : sureFormatla(gecen)}
        </span>
      </div>

      <button
        className={`callJoinBtn ${dolu ? "callJoinBtnDisabled" : ""}`}
        onClick={dolu ? undefined : () => callManager.katil(d.gelen.callId).catch(() => {})}
        disabled={dolu}
      >
        {dolu ? t("call.fullShort") : t("call.join")}
      </button>
    </div>
  );
}
